import { computed, ref } from 'vue'
import { DEFAULT_VIEWPORT, MAX_ZOOM, MIN_ZOOM, WORLD_ORIGIN } from '../model/constants'
import type { Point } from '../model/types'

/** Pan/zoom state of the canvas and conversions between screen and world coordinates. */
export function useFlowViewport() {
  const viewport = ref({ ...DEFAULT_VIEWPORT })
  const canvasEl = ref<HTMLElement | null>(null)
  /** Last pointer position over the canvas; pastes and shortcuts create nodes here. */
  const cursorWorld = ref<Point>({ x: 200, y: 200 })

  const toWorld = (event: MouseEvent): Point => {
    const canvas = canvasEl.value
    if (!canvas) return { x: event.clientX, y: event.clientY }
    const rect = canvas.getBoundingClientRect()
    return {
      x: (event.clientX - rect.left - viewport.value.x) / viewport.value.scale - WORLD_ORIGIN,
      y: (event.clientY - rect.top - viewport.value.y) / viewport.value.scale - WORLD_ORIGIN,
    }
  }

  /** World coordinates of the visible canvas centre. */
  const viewportCenter = (): Point => {
    const el = canvasEl.value
    const w = el ? el.clientWidth : 800
    const h = el ? el.clientHeight : 600
    const vp = viewport.value
    return {
      x: (w / 2 - vp.x) / vp.scale - WORLD_ORIGIN,
      y: (h / 2 - vp.y) / vp.scale - WORLD_ORIGIN,
    }
  }

  /** Zooms by `factor` keeping the given screen point (default: canvas centre) fixed. */
  const zoomAt = (factor: number, clientX?: number, clientY?: number) => {
    const canvas = canvasEl.value
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const cx = typeof clientX === 'number' ? clientX : rect.left + rect.width / 2
    const cy = typeof clientY === 'number' ? clientY : rect.top + rect.height / 2

    const oldScale = viewport.value.scale
    const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldScale * factor))
    if (newScale === oldScale) return

    const localX = cx - rect.left
    const localY = cy - rect.top
    const worldX = (localX - viewport.value.x) / oldScale
    const worldY = (localY - viewport.value.y) / oldScale
    viewport.value.scale = newScale
    viewport.value.x = localX - worldX * newScale
    viewport.value.y = localY - worldY * newScale
  }

  const onWheel = (event: WheelEvent) => {
    event.preventDefault()
    zoomAt(event.deltaY < 0 ? 1.08 : 1 / 1.08, event.clientX, event.clientY)
  }

  const resetView = () => {
    viewport.value = { ...DEFAULT_VIEWPORT }
  }

  const trackCursor = (event: MouseEvent) => {
    cursorWorld.value = toWorld(event)
  }

  /** Background grid that scales and scrolls with the world. */
  const gridStyle = computed(() => {
    const size = Math.max(6, 28 * viewport.value.scale)
    const offsetX = ((viewport.value.x % size) + size) % size
    const offsetY = ((viewport.value.y % size) + size) % size
    return {
      backgroundSize: `${size}px ${size}px`,
      backgroundPosition: `${offsetX}px ${offsetY}px`,
    }
  })

  return {
    viewport,
    canvasEl,
    cursorWorld,
    toWorld,
    viewportCenter,
    zoomAt,
    onWheel,
    resetView,
    trackCursor,
    gridStyle,
  }
}

export type FlowViewport = ReturnType<typeof useFlowViewport>
